import React from 'react';
import { BookOpen, Users, AlertCircle, FileText } from 'lucide-react';
export function DetailsSection() {
  return <section className="py-20 px-6 bg-[#FAFAF7]" data-id="element-51">
      <div className="max-w-[720px] mx-auto" data-id="element-52">
        {/* Objection Handling */}
        <div className="mb-20 fade-in-up" data-id="element-53">
          <h3 className="text-2xl md:text-3xl font-bold text-[#2B4257] mb-6" data-id="element-54">
            "But what if I regret it?"
          </h3>
          <p className="text-lg text-[#5A5A5A] mb-6 leading-relaxed" data-id="element-55">
            You might regret one or two choices.
          </p>
          <p className="text-lg text-[#1A1A1A] mb-6 leading-relaxed font-medium" data-id="element-56">
            But right now you're living with a <em data-id="element-57">guaranteed</em> regret every
            day:
          </p>

          <ul className="space-y-3 mb-8 pl-6 border-l-2 border-red-300" data-id="element-58">
            <li className="text-lg text-[#5A5A5A]" data-id="element-59">The stress</li>
            <li className="text-lg text-[#5A5A5A]" data-id="element-60">The pressure</li>
            <li className="text-lg text-[#5A5A5A]" data-id="element-61">The background shame</li>
            <li className="text-lg text-[#5A5A5A]" data-id="element-62">The tension in the house</li>
            <li className="text-lg text-[#5A5A5A]" data-id="element-63">
              The constant feeling that you're behind
            </li>
          </ul>

          <div className="bg-[#D4C5A9]/15 p-6 rounded-lg border-l-4 border-[#6B9B8A]" data-id="element-64">
            <p className="text-lg text-[#1A1A1A] font-medium leading-relaxed" data-id="element-65">
              This system is built to reduce regret by basing decisions on your
              values (<strong data-id="element-66">Legacy Map</strong>) and your pre-decided rules (
              <strong data-id="element-67">Decision Rules</strong>)—not your mood in the moment.
            </p>
          </div>
        </div>

        {/* What's Inside */}
        <div className="mb-20 fade-in-up delay-100" data-id="element-68">
          <div className="flex items-center mb-8" data-id="element-69">
            <BookOpen className="w-8 h-8 text-[#6B9B8A] mr-4" data-id="element-70" />
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B4257]" data-id="element-71">
              What you get (what's inside)
            </h2>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-sm border border-[#D4C5A9]/30" data-id="element-72">
            <h4 className="font-bold text-xl text-[#2B4257] mb-6 border-b border-[#D4C5A9]/20 pb-4" data-id="element-73">
              Downsizing Without Regret — Premium Edition (PDF)
            </h4>

            <p className="text-[#5A5A5A] mb-6" data-id="element-74">Inside you'll find:</p>

            <ul className="space-y-4 mb-8" data-id="element-75">
              <li className="text-[#5A5A5A] flex items-start" data-id="element-76">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-77">
                  ✓
                </span>
                <span data-id="element-78">
                  <strong className="text-[#1A1A1A]" data-id="element-79">The 2-phase plan:</strong>{' '}
                  Design → Execution
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-80">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-81">
                  ✓
                </span>
                <span data-id="element-82">
                  <strong className="text-[#1A1A1A]" data-id="element-83">
                    The Legacy Map template
                  </strong>{' '}
                  (and how to fill it out without getting sentimental-stuck)
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-84">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-85">
                  ✓
                </span>
                <span data-id="element-86">
                  <strong className="text-[#1A1A1A]" data-id="element-87">
                    The Decision Rules Sheet
                  </strong>{' '}
                  (plug-and-play rules you customize)
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-88">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-89">
                  ✓
                </span>
                <span data-id="element-90">
                  <strong className="text-[#1A1A1A]" data-id="element-91">
                    The HOLD (7 Days) method
                  </strong>{' '}
                  that prevents "maybe" piles from becoming permanent
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-92">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-93">
                  ✓
                </span>
                <span data-id="element-94">
                  <strong className="text-[#1A1A1A]" data-id="element-95">
                    The Dignity Protocol
                  </strong>{' '}
                  for the categories men freeze on (tools, awards, career items,
                  inherited objects)
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-96">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-97">
                  ✓
                </span>
                <span data-id="element-98">
                  <strong className="text-[#1A1A1A]" data-id="element-99">
                    The Family Sorting Meeting agenda
                  </strong>{' '}
                  (30 minutes) that prevents fights before they start
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-100">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-101">
                  ✓
                </span>
                <span data-id="element-102">
                  <strong className="text-[#1A1A1A]" data-id="element-103">
                    Simple scripts for spouse/kids
                  </strong>{' '}
                  so you don't get dragged into debate
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-104">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-105">
                  ✓
                </span>
                <span data-id="element-106">
                  <strong className="text-[#1A1A1A]" data-id="element-107">
                    The 45-Minute Execution Loop
                  </strong>{' '}
                  to finish faster without burnout
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-108">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-109">
                  ✓
                </span>
                <span data-id="element-110">
                  <strong className="text-[#1A1A1A]" data-id="element-111">
                    Hard category guides:
                  </strong>{' '}
                  papers, photos, collections, "dad stuff"
                </span>
              </li>
              <li className="text-[#5A5A5A] flex items-start" data-id="element-112">
                <span className="text-[#6B9B8A] mr-3 mt-1 flex-shrink-0" data-id="element-113">
                  ✓
                </span>
                <span data-id="element-114">
                  <strong className="text-[#1A1A1A]" data-id="element-115">
                    A backwards timeline + last-week checklist
                  </strong>{' '}
                  so the move doesn't become chaos
                </span>
              </li>
            </ul>

            <div className="bg-[#FAFAF7] p-5 rounded border border-[#D4C5A9]/30" data-id="element-116">
              <h5 className="font-bold text-[#2B4257] text-base mb-3 flex items-center" data-id="element-117">
                <FileText className="w-5 h-5 mr-2 text-[#6B9B8A]" data-id="element-118" />
                Printables included:
              </h5>
              <div className="flex flex-wrap gap-3" data-id="element-119">
                {['Decision Rules Sheet (1 page)', 'Legacy Map (1 page)', 'Family Sorting Meeting Agenda (1 page)', 'Last-week Checklist (1 page)'].map(item => <span key={item} className="text-sm font-medium bg-white px-3 py-1.5 rounded border border-[#D4C5A9]/20 text-[#5A5A5A]" data-id="element-120">
                    {item}
                  </span>)}
              </div>
            </div>
          </div>
        </div>

        {/* Who This Is For */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 fade-in-up delay-200" data-id="element-121">
          <div data-id="element-122">
            <div className="flex items-center mb-4" data-id="element-123">
              <Users className="w-6 h-6 text-[#2B4257] mr-3" data-id="element-124" />
              <h3 className="text-xl font-bold text-[#2B4257]" data-id="element-125">
                This is for you if:
              </h3>
            </div>
            <ul className="space-y-3" data-id="element-126">
              {["You're a man 65+ staring down a downsize or move", 'You\'re tired of "maybe piles" and stalled rooms', 'You want to stay in charge of your decisions', 'You want less tension with family', 'You want a clean finish without feeling stripped'].map((item, i) => <li key={i} className="flex items-start text-[#5A5A5A] text-base" data-id="element-127">
                  <CheckIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-128" />
                  <span data-id="element-129">{item}</span>
                </li>)}
            </ul>
          </div>

          <div data-id="element-130">
            <div className="flex items-center mb-4" data-id="element-131">
              <AlertCircle className="w-6 h-6 text-[#6B9B8A] mr-3" data-id="element-132" />
              <h3 className="text-xl font-bold text-[#2B4257]" data-id="element-133">
                This is NOT for you if:
              </h3>
            </div>
            <ul className="space-y-3" data-id="element-134">
              {['You want minimalist aesthetics', 'You want to declutter in one emotional weekend', "You're looking for therapy or crisis-level hoarding support"].map((item, i) => <li key={i} className="flex items-start text-[#5A5A5A] text-base" data-id="element-135">
                  <XIcon className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-136" />
                  <span data-id="element-137">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* CTA after what's inside */}
        <div className="text-center fade-in-up" data-id="element-138">
          <a href="#pricing" className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-200 bg-[#2B4257] rounded-md hover:bg-[#1f3040] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2B4257]" data-id="element-139">
            Get the Playbook — $6.36{' '}
            <span className="ml-2 line-through opacity-60 text-sm" data-id="element-140">$39</span>
          </a>
        </div>
      </div>
    </section>;
}
function CheckIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-141">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-142" />
    </svg>;
}
function XIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-143">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-144" />
    </svg>;
}